import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dangerIcon from "../../asserts/logo/Danger.svg";

/**
 * Figma: confirm permanent delete of a Derived risk.
 * Delete stays disabled until the acknowledgement checkbox is checked.
 */
export default function DeleteRiskDialog({
  risk,
  onClose,
  onConfirm,
  submitting,
  linkedMetricCount = 1,
  linkedTechniqueCount = 2,
}) {
  const open = Boolean(risk);
  const riskName = (risk?.name || "").trim() || "this risk";
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open, risk?.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          p: { xs: 2.5, sm: 3 },
          maxWidth: 480,
          position: "relative",
          boxShadow: "0 20px 40px rgba(16, 24, 40, 0.18)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        disabled={submitting}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: "#667085",
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ textAlign: "center", pt: 1 }}>
        <Box
          component="img"
          src={dangerIcon}
          alt="Danger"
          sx={{
            width: 43,
            height: 39,
            display: "inline-block",
            mb: 2,
          }}
        />

        <Typography
          sx={{
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 700,
            color: "#101828",
            lineHeight: 1.3,
            mb: 1.5,
            px: 1,
          }}
        >
          Delete {riskName}?
        </Typography>

        <Typography sx={{ fontSize: "14px", color: "#555555", lineHeight: "21px", mb: 1.5, px: 0.5 }}>
          This action can&apos;t be undone. The risk, its derived metrics, and any linked test
          techniques will be permanently removed from this gate.
        </Typography>

        <Typography sx={{ fontSize: "14px", color: "#555555", lineHeight: "21px", mb: 2, px: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: "var(--Danger-50, #DE350B)" }}>Note:</Box>{" "}
          This risk is currently linked to{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {linkedMetricCount} metric{linkedMetricCount === 1 ? "" : "s"}
          </Box>{" "}
          and{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {linkedTechniqueCount} test technique{linkedTechniqueCount === 1 ? "" : "s"}
          </Box>
          . They will be deleted with the risk.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, px: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={submitting}
                sx={{
                  color: "#D0D5DD",
                  "&.Mui-checked": { color: "#1A6EF5" },
                  width: 24,
                  height: 24,
                  p: 0,
                  mr: 1,
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px", color: "#555555", textAlign: "left", lineHeight: "21px" }}>
                I understand this is permanent and will be recorded in the audit log.
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              m: 0,
              maxWidth: 360,
              textAlign: "left",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#1A6EF5",
              borderColor: "#1A6EF5",
              borderWidth: "1px",
              borderRadius: "4px",
              width: 77,
              minWidth: 77,
              height: 40,
              p: "8px 16px",
              gap: "4px",
              opacity: 1,
              boxSizing: "border-box",
              "&:hover": { borderColor: "#1251c0", bgcolor: "rgba(26, 110, 245, 0.04)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={!acknowledged || submitting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "4px",
              width: 77,
              minWidth: 77,
              height: 40,
              p: "8px 16px",
              gap: "4px",
              opacity: 1,
              boxSizing: "border-box",
              border: "1px solid #005BF0",
              boxShadow: "none",
              bgcolor: "#005BF0",
              "&:hover": { bgcolor: "#004BCC", borderColor: "#004BCC", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "#F5F5F5",
                color: "#98A2B3",
                borderColor: "#E6E6E6",
                boxShadow: "0px 1px 2px 0px #0000000A",
                opacity: 1,
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
